import { getFormatNumPlus, getFormatPercentPlus } from '@gp/utils/common';
import {
  BASE_BAR_SERIES_OPTION,
  BASE_LINE_SERIES_OPTION,
  PRIMARY_CIRCLE_DECAL_OPTIONS,
  ECHART_COLOR,
} from '@gp/constants/echarts';
import {
  BUDGET,
  ACTUAL_FORECAST,
  ACTUAL,
  FORECAST,
  COMPLETE,
} from '@gp/constants/budgetActualOrForecastCompletionEChart';

export const isBudgetActualOrForecastCompleteNoData = (data: IBudgetActualOrForecastCompleteData[]): boolean => {
  if (!Array.isArray(data)) return true;
  if (!data.length) return true;
  for (const item of data) {
    const budget = item?.[BUDGET];
    const actualOrForecast = item?.[ACTUAL_FORECAST];
    const complete = item?.[COMPLETE];
    const flag1 = typeof budget === 'number' && !Number.isNaN(budget);
    const flag2 =
      typeof actualOrForecast === 'number' && !Number.isNaN(actualOrForecast);
    const flag3 = typeof complete === 'number' && !Number.isNaN(complete);
    if (flag1 || flag2 || flag3) {
      return false;
    }
  }
  return true;
};

export const isRevenueCompletionTableNoData = (data: IBudgetActualOrForecastCompleteTableData[]): boolean => {
  return !data?.length;
};

const getMetricsObj = (
  metrics: {
    name: string;
    value: any;
  }[],
  date = '',
): IBudgetActualOrForecastCompleteData => {
  const map: IBudgetActualOrForecastCompleteData = {
    date,
    [BUDGET]: NaN,
    [ACTUAL_FORECAST]: NaN,
    [ACTUAL]: NaN,
    [FORECAST]: NaN,
    [COMPLETE]: NaN,
    'actual_or_forecast': ACTUAL,
  };
  if (Array.isArray(metrics)) {
    for (const metric of metrics) {
      const metricKey = metric?.name?.toLowerCase();
      const metricValue = Number.parseFloat(metric?.value);
      if (map?.hasOwnProperty(metricKey)) {
        // @ts-ignore
        map[metricKey] = metricValue;
      }
      if (metric?.name === ACTUAL) {
        map[ACTUAL_FORECAST] = metricValue;
      }
      if (metric?.name === FORECAST && !map[ACTUAL_FORECAST] && metricValue) {
        map[ACTUAL_FORECAST] = metricValue;
        map['actual_or_forecast'] = FORECAST;
      }
    }
  }
  return map;
};

// 处理Middle - Long Time Financials模块 接口返回数据
export const handleDatas = (data: any): any => {
  if (!Array.isArray(data) || !data?.length) return null;
  try {
    const metrics: string[] = [],
      dataList: { [key: string]: string }[] = [];
    for (let index = 0; index < data?.length; index++) {
      const item = data?.[index];
      const metricList: { [key: string]: string } = {};
      for (let i = 0; i < item?.metrics_info?.length; i++) {
        const t: { name: string; value: string } = item?.metrics_info?.[i];
        metricList[t.name] = t?.value;
        metrics.includes(t.name) || metrics.push(t.name);
      }
      dataList.push({
        date: item.date,
        ...metricList,
      });
    }
    // 把Others固定排在最后
    if (metrics.includes('Others')) {
      const index = metrics.indexOf('Others');
      metrics.splice(index, 1);
      metrics.push('Others');
    }

    return {
      metrics,
      dataList,
    };
  } catch (error) {
    console.error(error);
    return null;
  }
};

// 处理Revenue Completion模块、Profit Completion模块 接口返回的 用于折线图数据
export const getBudgetActualForecastCompleteData = (
  data?: IStudioOverallDetailsRevenueProfitCompletionTrendData[],
): IBudgetActualOrForecastCompleteData[] => {
  const results: IBudgetActualOrForecastCompleteData[] = [];
  if (!Array.isArray(data)) return results;
  for (const item of data) {
    const date = item?.date;
    if (!date) continue;
    results.push(getMetricsObj(item?.metrics_info, date));
  }
  return results;
};

const getBudgetActualForecastCompleteDateMetricsMap = (
  data?: IStudioOverallDetailsRevenueProfitCompletionTrendData[],
): {
  [key: string]: IBudgetActualOrForecastCompleteData;
} => {
  const result: {
    [key: string]: IBudgetActualOrForecastCompleteData;
  } = {};
  if (Array.isArray(data)) {
    for (const item of data) {
      const date = item?.date;
      if (!date) continue;
      if (result?.hasOwnProperty(date)) continue;
      result[date] = getMetricsObj(item?.metrics_info, date);
    }
  }
  return result;
};

// 处理Revenue Completion模块 接口返回的数据 用于表格
export const getBudgetActualForecastCompleteTableData = (
  data?: IStudioOverallDetailsRevenueProfitCompletionTableData[],
): IBudgetActualOrForecastCompleteTableData[] => {
  const results: IBudgetActualOrForecastCompleteTableData[] = [];
  if (Array.isArray(data)) {
    for (const item of data) {
      const resultItem: IBudgetActualOrForecastCompleteTableData = {
        ...item,
        detail: getBudgetActualForecastCompleteDateMetricsMap(item?.detail),
      };
      results.push(resultItem);
    }
  }
  return results;
};

// Revenue Completion、Profit Completion 两个模块 双轴柱线图的 serie 核心配置
const REVENUE_COMPLETION_OR_PROFIT_COMPLETION_SERIE_METADATA: ISerieMetadataOfDoubleYAxisBarLine[] =
  [
    {
      key: ACTUAL_FORECAST,
      serie: {
        ...BASE_BAR_SERIES_OPTION,
        name: ACTUAL_FORECAST,
        yAxisIndex: 0,
        barGap: 0,
        color: '#2648A6',
      },
      yAxisValueFormatter: (value: number): string => getFormatNumPlus(value),
    },
    {
      key: BUDGET,
      serie: {
        ...BASE_BAR_SERIES_OPTION,
        name: BUDGET,
        yAxisIndex: 0,
        barGap: 0,
        itemStyle: {
          color: '#5086F3',
          decal: PRIMARY_CIRCLE_DECAL_OPTIONS,
        },
      },
      yAxisValueFormatter: (value: number): string => getFormatNumPlus(value),
    },
    {
      key: COMPLETE,
      serie: {
        ...BASE_LINE_SERIES_OPTION,
        name: COMPLETE,
        yAxisIndex: 1,
        symbol: 'circle',
        symbolSize: 6, // 拐点实心点大小
        color: ECHART_COLOR?.[7],
        emphasis: {
          // focus: 'series',
          scale: 1.25,
          itemStyle: {
            opacity: 1,
          },
        },
      },
      yAxisValueFormatter: (value: number): string =>
      getFormatPercentPlus(value, 0),
    },
  ];

export const getDoubleYAxisBarLineChartSerieMetadatasOfRevenueCompletionOrProfitCompletion =
  () => REVENUE_COMPLETION_OR_PROFIT_COMPLETION_SERIE_METADATA;

// Profit Completion模块 PDF下载表格的核心配置
const PROFIT_COMPLETION_PDF_DOWNLOAD_TABLE_METADATA: IPDFDownloadTableMetadata[] =
  [
    {
      key: BUDGET,
      header: 'Budget',
      formatter: getFormatNumPlus,
    },
    {
      key: ACTUAL_FORECAST,
      header: 'Actual or Forecast',
      formatter: getFormatNumPlus,
    },
    {
      key: COMPLETE,
      header: 'Complete',
      formatter: (value: number): string => getFormatPercentPlus(value, 0),
    },
  ];

// 获取Profit Completion模块 PDF下载表格的核心配置
export const getProfitCompletionPDFDownloadTableMetadata = () =>
  PROFIT_COMPLETION_PDF_DOWNLOAD_TABLE_METADATA;
